import { useEffect, useMemo, useRef, useState } from "react";
import "../../CSS/ReportViewer.css";
import api from "../../api";

/* ════════════════════════════════════════════════════════════
   REPORT VIEWER — wired to Ilms_SaamiBackend2026's real endpoints:
     ConsBookingController:      get-booking-places, get-partylistAll, get-job-list/{partyId}
     ReportDefinitionController: get-report-fields, get-report-definitions, get-report-data
   ════════════════════════════════════════════════════════════ */
const COMPANY = {
  name: "SAAMI TRADESTAR LOGISTICS PVT LTD",
  gstin: "27AABCS1234A1ZP",
  addr: "Plot 14, Transport Nagar, Bhiwandi, Maharashtra 421302",
};

// Presentational formatting hints for known numeric/date columns. The field
// list itself (id/name/cat/type) comes from GET /get-report-fields — this
// just decides *how* a "num" column is displayed (money vs plain vs weight).
const FMT_OVERRIDES = {
  invval: "money", freight: "money", other: "money", gst: "money", amount: "money", total: "money",
  chgwt: "wt", pcs: "int",
};

/* ─── FORMATTING HELPERS ─── */
function money(n) {
  return "₹" + (Math.round((n || 0) * 100) / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}
function fmtDate(d) {
  return d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
}
function fmtVal(col, raw) {
  if (!col) return raw;
  if (raw === "" || raw === undefined || raw === null) return "—";
  if (col.type === "date") return fmtDate(raw);
  if (col.type === "num") {
    if (col.fmt === "money") return money(raw);
    if (col.fmt === "wt" || col.fmt === "dec") return (+raw).toFixed(1);
    return String(raw);
  }
  return raw;
}
function fmtTotal(col, raw) {
  if (col.fmt === "money") return money(raw);
  if (col.fmt === "wt" || col.fmt === "dec") return (+raw).toFixed(1);
  return String(raw);
}
function totalsFor(cols, rows) {
  const t = {};
  cols.forEach((c) => {
    if (c.total && c.type === "num") t[c.id] = rows.reduce((a, r) => a + (+r[c.id] || 0), 0);
  });
  return t;
}
const BADGE_CLASS = {
  DELIVERED: "repvi-badge-delivered",
  PARTIAL: "repvi-badge-transit",
  PENDING: "repvi-badge-pending",
  ATTEMPTED: "repvi-badge-attempted",
  RETURNED: "repvi-badge-attempted",
  OPEN: "repvi-badge-pending",
};
const STATUS_LABEL = (s) => {
  if (!s) return "—";
  const key = String(s).toUpperCase();
  return key.charAt(0) + key.slice(1).toLowerCase();
};

function StatusBadge({ status }) {
  const key = status ? String(status).toUpperCase() : "";
  return <span className={`repvi-badge ${BADGE_CLASS[key] || ""}`}>{STATUS_LABEL(status)}</span>;
}

/* ─── DOWNLOAD / FILE HELPERS ─── */
function fileName(report, ext) {
  return report.name.replace(/[^a-z0-9]+/gi, "_") + "_" + report.partyName.replace(/[^a-z0-9]+/gi, "_") + "_" + report.ff + "_to_" + report.ft + "." + ext;
}
function downloadBlob(name, content, mime) {
  const blob = new Blob(["\ufeff" + content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/* Builds the raw HTML table markup shared by Excel export and Print/PDF. */
function reportTableHtml(report, forPrint) {
  const r = report;
  const head = "<tr>" + r.cols.map((c) => `<th class="${c.type === "num" ? "num" : ""}">${c.label}</th>`).join("") + "</tr>";
  const cell = (c, row) => {
    const v = row[c.id];
    if (c.id === "dlstatus") return `<td>${v}</td>`;
    const disp = fmtVal(c, v);
    return `<td class="${c.type === "num" ? "num" : ""}">${forPrint ? disp : (c.type === "num" && c.fmt === "money" ? String(v) : disp)}</td>`;
  };
  const sub = (rows, label, cls) => {
    const t = totalsFor(r.cols, rows);
    return `<tr class="${cls}">` + r.cols.map((c, i) => {
      if (i === 0) return `<td>${label}</td>`;
      if (c.total && c.type === "num") return `<td class="num">${forPrint ? fmtTotal(c, t[c.id]) : t[c.id]}</td>`;
      return `<td class="${c.type === "num" ? "num" : ""}"></td>`;
    }).join("") + "</tr>";
  };
  let body = "";
  if (r.groupByBranch) {
    const byB = {};
    r.rows.forEach((x) => { const key = x._branch || "Unspecified"; (byB[key] = byB[key] || []).push(x); });
    Object.keys(byB).sort().forEach((br) => {
      body += `<tr class="grp"><td colspan="${r.cols.length}">${br} — ${byB[br].length} consignments</td></tr>`;
      body += byB[br].map((row) => "<tr>" + r.cols.map((c) => cell(c, row)).join("") + "</tr>").join("");
      body += sub(byB[br], br + " Sub-total", "subtotal");
    });
  } else {
    body = r.rows.map((row) => "<tr>" + r.cols.map((c) => cell(c, row)).join("") + "</tr>").join("");
  }
  return { head, body, grand: sub(r.rows, "Grand Total", "grand") };
}

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════ */
export default function ReportViewer() {
  const [fields, setFields] = useState([]);           // from GET /get-report-fields
  const [defs, setDefs] = useState([]);                 // from GET /get-report-definitions
  const [parties, setParties] = useState([]);           // from GET /get-partylistAll
  const [jobs, setJobs] = useState([]);                  // from GET /get-job-list/{partyId}, for the selected party
  const [bookingPlaces, setBookingPlaces] = useState([]); // from GET /get-booking-places

  const fieldById = (id) => fields.find((f) => f.id === id);

  const [branch, setBranch] = useState(""); // Place_Id of the selected booking place, or "" for All
  const [party, setParty] = useState("");   // Party_id
  const [job, setJob] = useState("");       // Job_Id
  const [from, setFrom] = useState("2026-06-01");
  const [to, setTo] = useState("2026-06-30");
  const [groupByBranchChk, setGroupByBranchChk] = useState(false);
  const [running, setRunning] = useState(false);

  const [report, setReport] = useState(null);

  const [emailOpen, setEmailOpen] = useState(false);
  const [attachFmt, setAttachFmt] = useState("excel");
  const [emTo, setEmTo] = useState("");
  const [emCc, setEmCc] = useState("");
  const [emSubject, setEmSubject] = useState("");
  const [emBody, setEmBody] = useState("");

  const [toasts, setToasts] = useState([]);
  const toastId = useRef(0);
  const printAreaRef = useRef(null);

  function toast(msg, type) {
    const id = ++toastId.current;
    setToasts((t) => [...t, { id, msg, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 2900);
  }

  /* ── LOAD MASTER FIELD LIST, PARTIES & BOOKING PLACES ── */
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/get-report-fields');
        setFields(res.data || []);
      } catch { toast("Could not load field list from server", "err"); }
    })();
    (async () => {
      try {
        const res = await api.get('/get-partylistAll');
        const normalized = (res.data || []).map((p) => ({
          Party_id: p.party_id ?? p.Party_id,
          Party_Name: p.party_Name ?? p.Party_Name,
        }));
        setParties(normalized);
      } catch { toast("Could not load party list from server", "err"); }
    })();
    (async () => {
      try {
        const res = await api.get('/get-booking-places');
        const normalized = (res.data || []).map((b) => ({
          Place_Id: b.place_Id ?? b.Place_Id,
          Place_name: b.place_name ?? b.Place_name,
        }));
        setBookingPlaces(normalized);
      } catch { toast("Could not load booking places from server", "err"); }
    })();
    (async () => {
      try {
        const res = await api.get('/get-report-definitions');
        setDefs(res.data || []);
      } catch { toast("Could not load saved report definitions", "err"); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── LOAD JOBS FOR THE SELECTED PARTY ── */
  useEffect(() => {
    if (!party) { setJobs([]); return; }
    (async () => {
      try {
        const res = await api.get(`/get-job-list/${party}`);
        const normalized = (res.data || []).map((j) => ({
          Job_Id: j.job_Id ?? j.Job_Id,
          Job_Name: j.job_Name ?? j.Job_Name,
        }));
        setJobs(normalized);
      } catch { toast("Could not load job list from server", "err"); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [party]);

  function handlePartyChange(id) {
    setParty(id);
    setJob("");
    setReport(null);
  }

  /* ── RUN REPORT: pulls real rows for the saved definition's columns ── */
  async function runReport() {
    if (!party) { toast("Select a party", "warn"); return; }
    if (!job) { toast("Select a job", "warn"); return; }
    if (!from || !to) { toast("Select the period", "warn"); return; }

    const def = defs.find((d) => String(d.partyId) === String(party) && String(d.jobId) === String(job));
    if (!def) {
      toast('No saved report definition for this Party/Job — define one in Report Definition first', 'warn');
      setReport(null);
      return;
    }

    setRunning(true);
    try {
      const params = { partyId: party, jobId: job, from, to };
      if (branch) params.bookPlaceId = branch;
      const res = await api.get('/get-report-data', { params });
      const data = res.data;
      const cols = def.cols.map((c) => {
        const f = fieldById(c.id) || { type: 'text' };
        return { ...c, type: f.type, fmt: FMT_OVERRIDES[c.id] };
      });

      const p = parties.find((x) => String(x.Party_id) === String(party));
      const j = jobs.find((x) => String(x.Job_Id) === String(job));
      const bp = bookingPlaces.find((x) => String(x.Place_Id) === String(branch));

      setReport({
        party, job, branch: bp ? bp.Place_name : '', ff: from, ft: to,
        cols, rows: data.rows || [],
        groupByBranch: groupByBranchChk && !branch,
        name: def.name, hasDef: true,
        partyName: p ? p.Party_Name : `Party #${party}`,
        jobName: j ? j.Job_Name : `Job #${job}`,
      });
    } catch (e) {
      console.error('runReport error:', e);
      toast('Could not run report: ' + (e.response?.data?.message || e.message), 'err');
      setReport(null);
    } finally {
      setRunning(false);
    }
  }

  /* ── EXPORT: EXCEL ── */
  function exportExcelSilent(rep) {
    const t = reportTableHtml(rep, false);
    const meta = `Report: ${rep.name} | Party: ${rep.partyName} | Job: ${rep.jobName} | Branch: ${rep.branch || "All"} | Period: ${fmtDate(rep.ff)} to ${fmtDate(rep.ft)}`;
    const html = `<html xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8">
      <style>table{border-collapse:collapse;}th,td{border:1px solid #999;padding:4px 8px;font-family:Calibri,Arial;font-size:11px;}
      th{background:#1a2a4a;color:#fff;}.num{mso-number-format:"0.00";text-align:right;}tr.subtotal td{background:#e0e7ff;font-weight:bold;}tr.grand td{background:#1a2a4a;color:#fff;font-weight:bold;}tr.grp td{background:#334155;color:#fff;font-weight:bold;}</style></head>
      <body><h3>${COMPANY.name}</h3><div>${meta}</div><br>
      <table><thead>${t.head}</thead><tbody>${t.body}</tbody><tfoot>${t.grand}</tfoot></table></body></html>`;
    downloadBlob(fileName(rep, "xls"), html, "application/vnd.ms-excel");
  }
  function exportExcel() {
    if (!report) return;
    exportExcelSilent(report);
    toast("Excel exported", "ok");
  }

  /* ── EXPORT: PDF (print) ── */
  function buildPrintArea() {
    const r = report, t = reportTableHtml(r, true);
    if (printAreaRef.current) {
      printAreaRef.current.innerHTML = `
        <div class="repvi-pa-hdr">
          <div><div class="repvi-pa-co">${COMPANY.name}</div><div class="repvi-pa-co-sub">${COMPANY.addr} · GSTIN ${COMPANY.gstin}</div></div>
          <div><div class="repvi-pa-rt">${r.name}</div><div class="repvi-pa-meta">Generated ${fmtDate(new Date().toISOString())}</div></div>
        </div>
        <div class="repvi-pa-filters"><b>Party:</b> ${r.partyName} &nbsp;|&nbsp; <b>Job:</b> ${r.jobName} &nbsp;|&nbsp; <b>Branch:</b> ${r.branch || "All Branches"} &nbsp;|&nbsp; <b>Period:</b> ${fmtDate(r.ff)} – ${fmtDate(r.ft)} &nbsp;|&nbsp; <b>Records:</b> ${r.rows.length}</div>
        <table class="repvi-pa-tbl"><thead>${t.head}</thead><tbody>${t.body}</tbody><tfoot>${t.grand}</tfoot></table>`;
    }
  }
  function exportPdf() {
    if (!report) return;
    buildPrintArea();
    toast('Opening print dialog — choose "Save as PDF"', "info");
    setTimeout(() => window.print(), 250);
  }

  /* ── EMAIL ── */
  function openEmail() {
    if (!report) return;
    const r = report;
    setEmSubject(`${r.name} — ${r.partyName} (${fmtDate(r.ff)} to ${fmtDate(r.ft)})`);
    setEmBody(`Dear Team,\n\nPlease find attached the ${r.name} for ${r.partyName} / ${r.jobName} (${r.branch || "All Branches"}) for the period ${fmtDate(r.ff)} to ${fmtDate(r.ft)}.\n\nTotal consignments: ${r.rows.length}\nTotal business: ${money(r.rows.reduce((a, x) => a + (+x.total || 0), 0))}\n\nRegards,\n${COMPANY.name}`);
    setEmTo("");
    setEmCc("");
    setAttachFmt("excel");
    setEmailOpen(true);
  }
  function sendEmail() {
    const to = emTo.trim();
    if (!to) { toast("Enter at least one recipient", "warn"); return; }
    if (attachFmt === "excel" || attachFmt === "both") exportExcelSilent(report);
    setEmailOpen(false);
    const list = to.split(",");
    toast("Report emailed to " + list[0] + (list.length > 1 ? " +" + (list.length - 1) : "") + " (" + attachFmt + ")", "ok");
  }
  function openMailClient() {
    const sub = encodeURIComponent(emSubject);
    const body = encodeURIComponent(emBody + "\n\n(Attach the downloaded report file before sending.)");
    window.location.href = `mailto:${emTo.trim()}?cc=${emCc.trim()}&subject=${sub}&body=${body}`;
  }

  /* ── DERIVED SUMMARY ── */
  const summary = report ? {
    cnr: report.rows.length,
    pcs: report.rows.reduce((a, x) => a + (+x.pcs || 0), 0).toLocaleString("en-IN"),
    wt: report.rows.reduce((a, x) => a + (+x.chgwt || 0), 0).toFixed(1),
    amt: money(report.rows.reduce((a, x) => a + (+x.total || 0), 0)).replace(".00", ""),
  } : null;

  const grandTotals = report ? totalsFor(report.cols, report.rows) : null;
  const groupedRows = useMemo(() => {
    if (!report || !report.groupByBranch) return null;
    const byBranch = {};
    report.rows.forEach((row) => { const key = row._branch || "Unspecified"; (byBranch[key] = byBranch[key] || []).push(row); });
    return Object.keys(byBranch).sort().map((br) => ({ branch: br, rows: byBranch[br] }));
  }, [report]);

  return (
    <div className="repvi-page">
      {/* PAGE HEADER */}
      <div className="repvi-page-header">
        <div>
          <div className="repvi-page-title">Branch / Party / Job <span>Business Report</span></div>
          <div className="repvi-page-sub">Generate period-wise business details as per the saved report definition, then export or email.</div>
        </div>
      </div>

      {/* FILTERS */}
      <div className="repvi-panel">
        <div className="repvi-panel-head">
          <div className="repvi-panel-icon">🎯</div>
          <div>
            <div className="repvi-panel-title">Report Criteria</div>
            <div className="repvi-panel-sub">Branch · Party · Job · Period</div>
          </div>
        </div>
        <div className="repvi-panel-body">
          <div className="repvi-filters">
            <div className="repvi-field">
              <label>Branch</label>
              <select value={branch} onChange={(e) => setBranch(e.target.value)}>
                <option value="">All Branches</option>
                {bookingPlaces.map((b) => <option key={b.Place_Id} value={b.Place_Id}>{b.Place_name}</option>)}
              </select>
            </div>
            <div className="repvi-field">
              <label>Party <span className="repvi-req">*</span></label>
              <select value={party} onChange={(e) => handlePartyChange(e.target.value)}>
                <option value="">— Select Party —</option>
                {parties.map((p) => <option key={p.Party_id} value={p.Party_id}>{p.Party_Name}</option>)}
              </select>
            </div>
            <div className="repvi-field">
              <label>Job <span className="repvi-req">*</span></label>
              <select value={job} disabled={!party} onChange={(e) => setJob(e.target.value)}>
                <option value="">— Select Job —</option>
                {jobs.map((j) => <option key={j.Job_Id} value={j.Job_Id}>{j.Job_Name}</option>)}
              </select>
            </div>
            <div className="repvi-field">
              <label>From Date <span className="repvi-req">*</span></label>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="repvi-field">
              <label>To Date <span className="repvi-req">*</span></label>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
            <div className="repvi-field">
              <label>&nbsp;</label>
              <button className="repvi-btn repvi-btn-primary" onClick={runReport} disabled={running}>{running ? '… Running' : '▶ Run Report'}</button>
            </div>
          </div>
          <div className="repvi-filters-row2">
            <label className="repvi-chkbox">
              <input type="checkbox" checked={groupByBranchChk} onChange={(e) => setGroupByBranchChk(e.target.checked)} />
              Group by Branch (with sub-totals)
            </label>
            <span className="repvi-hint">Columns &amp; totals come from the report definition for the selected Party + Job.</span>
          </div>
        </div>
      </div>

      {/* SUMMARY */}
      {summary && (
        <div className="repvi-summary-grid">
          <div className="repvi-summary-card" style={{ "--card-accent": "var(--accent2)" }}>
            <div className="repvi-summary-val">{summary.cnr}</div>
            <div className="repvi-summary-lbl">Consignments</div>
          </div>
          <div className="repvi-summary-card" style={{ "--card-accent": "var(--accent2)" }}>
            <div className="repvi-summary-val">{summary.pcs}</div>
            <div className="repvi-summary-lbl">Total Pcs</div>
          </div>
          <div className="repvi-summary-card" style={{ "--card-accent": "var(--success)" }}>
            <div className="repvi-summary-val">{summary.wt}</div>
            <div className="repvi-summary-lbl">Charged Weight (Kg)</div>
          </div>
          <div className="repvi-summary-card" style={{ "--card-accent": "var(--accent)" }}>
            <div className="repvi-summary-val">{summary.amt}</div>
            <div className="repvi-summary-lbl">Total Business (₹)</div>
          </div>
        </div>
      )}

      {/* REPORT OUTPUT */}
      <div className="repvi-panel">
        <div className="repvi-panel-head">
          <div className="repvi-panel-icon">📄</div>
          <div>
            <div className="repvi-panel-title">Report Output</div>
            <div className="repvi-panel-sub">{report ? `${report.name} · ${report.rows.length} records` : "Run a report to see results"}</div>
          </div>
          <div className="repvi-panel-right">
            <button className="repvi-btn repvi-btn-success repvi-btn-sm" disabled={!report || report.rows.length === 0} onClick={exportExcel}>⬇ Excel</button>
            <button className="repvi-btn repvi-btn-danger repvi-btn-sm" disabled={!report || report.rows.length === 0} onClick={exportPdf}>🖨 PDF</button>
            <button className="repvi-btn repvi-btn-accent2 repvi-btn-sm" disabled={!report || report.rows.length === 0} onClick={openEmail}>✉ Email</button>
          </div>
        </div>

        {report && (
          <div className="repvi-meta">
            <div className="repvi-meta-item">Report: <b>{report.name}</b></div>
            <div className="repvi-meta-item">Party: <b>{report.partyName}</b></div>
            <div className="repvi-meta-item">Job: <b>{report.jobName}</b></div>
            <div className="repvi-meta-item">Branch: <b>{report.branch || "All Branches"}</b></div>
            <div className="repvi-meta-item">Period: <b>{fmtDate(report.ff)} – {fmtDate(report.ft)}</b></div>
          </div>
        )}

        {report && !report.hasDef && (
          <div className="repvi-def-note">⚠ No saved report definition for this Party/Job — showing the standard column set. Define one in <b>&nbsp;Report Definition</b>.</div>
        )}

        {!report && (
          <div className="repvi-empty">
            <div className="repvi-empty-ico">📊</div>
            <div className="repvi-empty-t">No report yet</div>
            <div className="repvi-empty-s">Select Party, Job &amp; period, then click <b>Run Report</b></div>
          </div>
        )}

        {report && report.rows.length === 0 && (
          <div className="repvi-empty">
            <div className="repvi-empty-ico">🔍</div>
            <div className="repvi-empty-t">No records found</div>
            <div className="repvi-empty-s">No consignments match this party / job / branch / period</div>
          </div>
        )}

        {report && report.rows.length > 0 && (
          <div className="repvi-tbl-scroll">
            <table className="repvi-tbl">
              <thead>
                <tr>
                  {report.cols.map((c) => <th key={c.id} className={c.type === "num" ? "repvi-num" : ""}>{c.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {report.groupByBranch
                  ? groupedRows.flatMap((g) => {
                      const t = totalsFor(report.cols, g.rows);
                      return [
                        <tr key={`grp-${g.branch}`} className="repvi-grp">
                          <td colSpan={report.cols.length}>📍 {g.branch} — {g.rows.length} consignments</td>
                        </tr>,
                        ...g.rows.map((row, idx) => (
                          <tr key={row.cnr + idx}>
                            {report.cols.map((c) => (
                              <td key={c.id} className={c.type === "num" ? "repvi-num" : ""}>
                                {c.id === "dlstatus" ? <StatusBadge status={row[c.id]} /> : fmtVal(c, row[c.id])}
                              </td>
                            ))}
                          </tr>
                        )),
                        <tr key={`sub-${g.branch}`} className="repvi-subtotal">
                          {report.cols.map((c, i) => (
                            <td key={c.id} className={c.type === "num" ? "repvi-num" : ""}>
                              {i === 0 ? `${g.branch} Sub-total` : (c.total && c.type === "num" ? fmtTotal(c, t[c.id]) : "")}
                            </td>
                          ))}
                        </tr>,
                      ];
                    })
                  : report.rows.map((row, idx) => (
                      <tr key={row.cnr + idx}>
                        {report.cols.map((c) => (
                          <td key={c.id} className={c.type === "num" ? "repvi-num" : ""}>
                            {c.id === "dlstatus" ? <StatusBadge status={row[c.id]} /> : fmtVal(c, row[c.id])}
                          </td>
                        ))}
                      </tr>
                    ))}
              </tbody>
              <tfoot>
                <tr className="repvi-grand">
                  {report.cols.map((c, i) => (
                    <td key={c.id} className={c.type === "num" ? "repvi-num" : ""}>
                      {i === 0 ? "Grand Total" : (c.total && c.type === "num" ? fmtTotal(c, grandTotals[c.id]) : "")}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* EMAIL MODAL */}
      {emailOpen && (
        <div className="repvi-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setEmailOpen(false); }}>
          <div className="repvi-modal">
            <div className="repvi-modal-head">
              <div className="repvi-modal-icon">✉</div>
              <div>
                <div className="repvi-modal-title">Email Report</div>
                <div className="repvi-modal-sub">{report ? report.name : "—"}</div>
              </div>
              <button className="repvi-modal-x" onClick={() => setEmailOpen(false)}>✕</button>
            </div>
            <div className="repvi-modal-body">
              <div className="repvi-field" style={{ marginBottom: 13 }}>
                <label>To <span className="repvi-req">*</span></label>
                <input type="text" value={emTo} onChange={(e) => setEmTo(e.target.value)} placeholder="name@company.com, ..." />
              </div>
              <div className="repvi-field" style={{ marginBottom: 13 }}>
                <label>CC</label>
                <input type="text" value={emCc} onChange={(e) => setEmCc(e.target.value)} placeholder="optional" />
              </div>
              <div className="repvi-field" style={{ marginBottom: 13 }}>
                <label>Subject</label>
                <input type="text" value={emSubject} onChange={(e) => setEmSubject(e.target.value)} />
              </div>
              <div className="repvi-field" style={{ marginBottom: 13 }}>
                <label>Message</label>
                <textarea rows={4} value={emBody} onChange={(e) => setEmBody(e.target.value)} />
              </div>
              <div className="repvi-field">
                <label>Attachment</label>
                <div className="repvi-attach-opts">
                  <div className={`repvi-attach-opt${attachFmt === "excel" ? " repvi-sel" : ""}`} onClick={() => setAttachFmt("excel")}>
                    <span className="repvi-attach-ico">📊</span><span className="repvi-attach-name">Excel</span>
                  </div>
                  <div className={`repvi-attach-opt${attachFmt === "pdf" ? " repvi-sel" : ""}`} onClick={() => setAttachFmt("pdf")}>
                    <span className="repvi-attach-ico">📄</span><span className="repvi-attach-name">PDF</span>
                  </div>
                  <div className={`repvi-attach-opt${attachFmt === "both" ? " repvi-sel" : ""}`} onClick={() => setAttachFmt("both")}>
                    <span className="repvi-attach-ico">🗂️</span><span className="repvi-attach-name">Both</span>
                  </div>
                </div>
                <div className="repvi-attach-note">The report will be attached and sent via the system mailer. You can also download the file and attach it from your own mail client.</div>
              </div>
            </div>
            <div className="repvi-modal-foot">
              <button className="repvi-btn repvi-btn-outline" onClick={() => setEmailOpen(false)}>Cancel</button>
              <button className="repvi-btn repvi-btn-outline" onClick={openMailClient}>📤 Open in Mail App</button>
              <button className="repvi-btn repvi-btn-success" onClick={sendEmail}>➤ Send</button>
            </div>
          </div>
        </div>
      )}

      {/* PRINT AREA (hidden, filled just before window.print) */}
      <div className="repvi-print-area" ref={printAreaRef}></div>

      {/* TOASTS */}
      <div className="repvi-toast-wrap">
        {toasts.map((t) => {
          const icons = { ok: "✓", err: "✕", warn: "⚠", info: "ℹ" };
          const cls = { ok: "repvi-toast-ok", err: "repvi-toast-err", warn: "repvi-toast-warn", info: "repvi-toast-info" };
          return (
            <div key={t.id} className={`repvi-toast ${cls[t.type] || "repvi-toast-info"}`}>
              <span style={{ fontSize: 15 }}>{icons[t.type] || "ℹ"}</span>{t.msg}
            </div>
          );
        })}
      </div>
    </div>
  );
}